import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Box, Stack, Button, CircularProgress } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ISwitchWithCustomText } from '../Switch/SwitchWithCustomText';
import { IChangePayload, ISubscriptionPlan } from '../../redux/subscription/types';
import PlanCard from './PlanCard';
import { PlanAdditionModal } from './PlanAdditionModal';
import { IPaymentCard } from '../../types/global/generic.types';
import { DialogueSheet } from '../DialogueSheet/DialogueSheet';
import { CreatorSubscriptionManageModal } from './CreatorSubscriptionManageModal';
import {
    isCreatorCatalogPlanSelected,
    hasManageableCreatorSubscription,
    shouldShowCreatorSubscriptionManage,
    shouldShowCreatorCanceledSubscriptionStrip,
    filterPremiumCampaignsLostOnPlan,
    isTerminalCreatorSubscriptionStatus,
} from '../../utils/creatorSubscriptionApi';
import { formatCreatorSubscriptionDate } from '../../utils/creatorSubscriptionFormat';
import {
    planGrantsAllEntitlements,
    planGrantsAnyEntitlement,
    planMatchesAllowedClasses,
    resolveCreatorEntitlements,
    type CreatorPlanClass,
} from '../../utils/planEntitlements';
import {
    CREATOR_SUBSCRIPTION_MANAGE_COPY,
    formatPremiumCampaignImpactBody,
    getCreatorCanceledStripSubtitle,
} from '../../constants/creatorSubscriptionManageCopy';
import { MIXPANEL_EVENT, trackSubscriptionAction } from '../../analytics/Mixpanel';
import { shouldRequestCreatorTrial } from '../../utils/creatorSubscriptionTrial';

interface ISortedPlan {
    year: ISubscriptionPlan[];
    month: ISubscriptionPlan[];
}

export interface ISettingsProps extends Omit<ISwitchWithCustomText, 'header' | 'body' | 'theme'> {
    onChange?: (payload: IChangePayload) => void;
    onCancel?: () => void;
    onUpdatePayment?: () => void;
    onReactivate?: () => void;
    cancelling?: boolean;
    reactivating?: boolean;
    updatingPayment?: boolean;
    fetchingSubscription?: boolean;
    plans?: ISubscriptionPlan[];
    initialPlan?: ISubscriptionPlan;
    userCards?: IPaymentCard[];
    isPromptMode?: boolean;
    loading?: boolean;
    paystackPublicKey?: string;
    paystackCallbackUrl?: string;
    /** When true, hosted checkout uses Flutterwave initialize/verify instead of Paystack. */
    isFlutterwaveRail?: boolean;
    /** After Paystack/Flutterwave saved-card subscribe (no redirect). */
    onHostedSubscribeSuccess?: () => void;
    /** Re-open plan modal after Flutterwave add-card return (plan_code). */
    resumePlanCode?: string | null;
    /** After add-card return, select the newest rail card. */
    resumeSelectNewestCard?: boolean;
    onResumePlanConsumed?: () => void;
    onResumeSelectNewestConsumed?: () => void;
    /** Called when user selects a plan (payment modal opens) — use to pause a countdown. */
    onPlanSelect?: VoidFunction;
    /** Called when user dismisses the payment modal without subscribing — use to resume a countdown. */
    onPlanDeselect?: VoidFunction;
    /** When provided, called instead of opening the plan modal — use to prompt login for unauthenticated users. */
    onAuthRequired?: VoidFunction;
    /**
     * When set, only show catalog plans that grant at least one of these
     * entitlements (from backend `effective_entitlements`).
     */
    requireAnyEntitlement?: Array<import('../../utils/planEntitlements').CreatorEntitlementKey>;
    /**
     * When set, only show plans that grant every listed entitlement
     * (e.g. premium campaigns needing Instagram automation + auto-upload).
     */
    requireAllEntitlements?: Array<import('../../utils/planEntitlements').CreatorEntitlementKey>;
    /** When set, only show these plan classes (e.g. Pro Pass → Pro + Pro Plus). */
    allowedPlanClasses?: CreatorPlanClass[];
}

/** Normalises Paystack "monthly"/"yearly" to Stripe-style "month"/"year" */
function normaliseInterval(interval?: string): string {
    if (!interval) return '';
    return interval.replace('monthly', 'month').replace('yearly', 'year');
}

/** Returns the plan code to use for matching plan cards.
 *  Paystack subscriptions expose it as plan.plan_code; Stripe uses plan.id (string). */
function resolvePlanCode(sub?: ISubscriptionPlan): string | null {
    if (!sub) return null;
    // Paystack: nested plan object has a plan_code field
    if (sub.plan?.plan_code) return sub.plan.plan_code;
    // Stripe: plan.id is the price ID string
    if (sub.plan?.id != null) return String(sub.plan.id);
    // Fallback to root-level plan_code
    return sub.plan_code ?? null;
}

function sortPlans(plans?: ISubscriptionPlan[]) {
    if (!plans) return {};
    // Separate plans into monthly and yearly based on interval
    const monthlyPlans = plans.filter(plan => plan.interval === 'month');
    const yearlyPlans = plans.filter(plan => plan.interval === 'year');

    // Sort each category by amount in ascending order
    const sortedMonthlyPlans = monthlyPlans.sort((a, b) => a.amount - b.amount);
    const sortedYearlyPlans = yearlyPlans.sort((a, b) => a.amount - b.amount);

    // Return the sorted plans in an object
    return {
        monthly: sortedMonthlyPlans,
        yearly: sortedYearlyPlans,
    };
}

const AccountSubscriptions: React.FC<ISettingsProps> = ({
    plans,
    initialPlan,
    onChange,
    onCancel,
    onUpdatePayment,
    onReactivate,
    cancelling,
    reactivating,
    updatingPayment,
    fetchingSubscription,
    userCards,
    isPromptMode,
    loading,
    paystackPublicKey,
    paystackCallbackUrl,
    isFlutterwaveRail,
    onHostedSubscribeSuccess,
    resumePlanCode,
    resumeSelectNewestCard,
    onResumePlanConsumed,
    onResumeSelectNewestConsumed,
    onPlanSelect,
    onPlanDeselect,
    onAuthRequired,
    requireAnyEntitlement,
    requireAllEntitlements,
    allowedPlanClasses,
}) => {
    const { themeStyle, darkModeEnabled } = useThemeContext();
    const showManageSubscription =
        shouldShowCreatorSubscriptionManage(initialPlan) &&
        !requireAnyEntitlement?.length &&
        !requireAllEntitlements?.length &&
        !allowedPlanClasses?.length;
    const showCanceledStrip =
        shouldShowCreatorCanceledSubscriptionStrip(initialPlan) &&
        !requireAnyEntitlement?.length &&
        !requireAllEntitlements?.length &&
        !allowedPlanClasses?.length;
    const canceledPeriodEnd = initialPlan?.current_period_end ?? initialPlan?.next_payment_date;
    const canceledEndLabel = formatCreatorSubscriptionDate(canceledPeriodEnd);
    const canceledStillInPeriod = Boolean(
        canceledPeriodEnd &&
            Number.isFinite(Date.parse(canceledPeriodEnd)) &&
            Date.parse(canceledPeriodEnd) > Date.now(),
    );
    const [manageModalOpen, setManageModalOpen] = useState(false);
    const [selectedPreliminaryPlan, setSelectedPreliminaryPlan] = useState<ISubscriptionPlan>();
    const [showDowngradeModal, setShowDowngradeModal] = useState(false);
    const [downgradeCampaignTitles, setDowngradeCampaignTitles] = useState<string[]>([]);
    const prevCancelling = React.useRef(false);
    React.useEffect(() => {
        if (prevCancelling.current && !cancelling) {
            setManageModalOpen(false);
        }
        prevCancelling.current = !!cancelling;
    }, [cancelling]);
    const [plan, setPlan] = useState<ISortedPlan>({
        month: [],
        year: [],
    });
    const [visisbleTab, setVisibleTab] = useState('year');
    const [open, setOpen] = useState(false);

    const filterAndSortPlans = useCallback(() => {
        const { monthly, yearly } = sortPlans(plans);

        // Always filter out free/system plans (amount === 0 or system_subscription flag)
        let filteredMonthly = (monthly as ISubscriptionPlan[]).filter(p => p.amount > 0 && !p.system_subscription);
        let filteredYearly = (yearly as ISubscriptionPlan[]).filter(p => p.amount > 0 && !p.system_subscription);

        if (requireAllEntitlements?.length) {
            filteredMonthly = filteredMonthly.filter(p => planGrantsAllEntitlements(p, requireAllEntitlements));
            filteredYearly = filteredYearly.filter(p => planGrantsAllEntitlements(p, requireAllEntitlements));
        } else if (requireAnyEntitlement?.length) {
            filteredMonthly = filteredMonthly.filter(p => planGrantsAnyEntitlement(p, requireAnyEntitlement));
            filteredYearly = filteredYearly.filter(p => planGrantsAnyEntitlement(p, requireAnyEntitlement));
        }

        if (allowedPlanClasses?.length) {
            filteredMonthly = filteredMonthly.filter(p => planMatchesAllowedClasses(p, allowedPlanClasses));
            filteredYearly = filteredYearly.filter(p => planMatchesAllowedClasses(p, allowedPlanClasses));
        }

        setPlan({
            month: filteredMonthly,
            year: filteredYearly,
        });
        const activePlanCode = resolvePlanCode(initialPlan);
        const activeInterval = normaliseInterval(initialPlan?.plan?.interval);
        const currentPlan = initialPlan && filteredMonthly.find(p => p.plan_code === activePlanCode);
        if (initialPlan && (!!currentPlan || activeInterval === 'month')) {
            setVisibleTab('month');
        } else {
            setVisibleTab('year');
        }
    }, [plans, initialPlan, requireAnyEntitlement, requireAllEntitlements, allowedPlanClasses]);

    useEffect(() => {
        filterAndSortPlans();
    }, [filterAndSortPlans]);

    // Resume subscribe modal after Flutterwave Billing add-card redirect.
    useEffect(() => {
        if (!resumePlanCode || !plans?.length) return;
        const match = plans.find(p => p.plan_code === resumePlanCode || String(p.plan?.id ?? '') === resumePlanCode);
        if (!match) return;
        setSelectedPreliminaryPlan(match);
        setOpen(true);
        onPlanSelect?.();
        onResumePlanConsumed?.();
    }, [resumePlanCode, plans, onPlanSelect, onResumePlanConsumed]);

    // Stripe confirm: card already selected in modal
    const handleOnProceed = (selectedCard?: IPaymentCard) => {
        onChange &&
            onChange({
                priceId: selectedPreliminaryPlan?.plan_code!,
                paymentMethodId:
                    !!selectedCard && selectedCard.payment_method ? selectedCard.payment_method : 'default',
                isUpgrade: hasManageableCreatorSubscription(initialPlan),
                start_trial: shouldRequestCreatorTrial(selectedPreliminaryPlan?.trial, {
                    hasManageableSubscription: hasManageableCreatorSubscription(initialPlan),
                }),
            });
        setOpen(false);
        setSelectedPreliminaryPlan(undefined);
    };

    const handleDowngradeModal = () => {
        setShowDowngradeModal(!showDowngradeModal);
    };

    const isYearly = visisbleTab === 'year';
    const t = theme[themeStyle];
    const visiblePlanCount = plan[visisbleTab as keyof ISortedPlan]?.length ?? 0;
    const promptColumns = Math.min(Math.max(visiblePlanCount, 1), 3);

    if (fetchingSubscription && !initialPlan) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8, width: '100%' }}>
                <CircularProgress sx={{ color: t.highlight }} />
            </Box>
        );
    }

    return (
        <Box
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
            }}
            sx={{
                gap: 3,
                width: '100%',
                height: '100%',
                mx: 0,
                position: 'relative',
                p: {
                    xs: 1,
                    sm: 0,
                },
                boxSizing: 'border-box',
            }}>
            {loading ? (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(14,14,14,0.55)',
                        borderRadius: 2,
                        pointerEvents: 'all',
                    }}>
                    <CircularProgress size={36} sx={{ color: t.highlight }} />
                </Box>
            ) : null}
            {!isPromptMode && (
                <Box sx={{ width: '100%', maxWidth: '36rem' }}>
                    <Typography
                        sx={{
                            fontSize: '0.8125rem',
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            color: t.tag_white,
                            opacity: 0.65,
                            lineHeight: 1.5,
                            maxWidth: '36rem',
                        }}>
                        Choose the plan that fits your stage in the industry. From emerging talent to global production
                        houses.
                    </Typography>
                </Box>
            )}

            <PlanAdditionModal
                userCards={userCards}
                onProceedClick={handleOnProceed}
                onClose={() => {
                    setOpen(!open);
                    onResumeSelectNewestConsumed?.();
                    onPlanDeselect?.();
                }}
                onCancelClick={() => {
                    setOpen(!open);
                    setSelectedPreliminaryPlan(undefined);
                    onResumeSelectNewestConsumed?.();
                    onPlanDeselect?.();
                }}
                onHostedSubscribeSuccess={() => {
                    setOpen(false);
                    setSelectedPreliminaryPlan(undefined);
                    onResumeSelectNewestConsumed?.();
                    onHostedSubscribeSuccess?.();
                }}
                open={open}
                plan={selectedPreliminaryPlan}
                isPromptMode={isPromptMode}
                preferNewestCard={Boolean(resumeSelectNewestCard)}
                isPaystack={
                    !isFlutterwaveRail &&
                    (selectedPreliminaryPlan?.source === 'paystack' || selectedPreliminaryPlan?.source !== 'stripe')
                }
                isFlutterwave={selectedPreliminaryPlan?.source === 'flutterwave' || Boolean(isFlutterwaveRail)}
                paystackPublicKey={paystackPublicKey}
                paystackCallbackUrl={paystackCallbackUrl}
            />

            {showManageSubscription && initialPlan && (
                <CreatorSubscriptionManageModal
                    open={manageModalOpen}
                    onClose={() => setManageModalOpen(false)}
                    subscription={initialPlan}
                    catalogPlans={plans}
                    cancelling={cancelling}
                    updatingPayment={updatingPayment}
                    reactivating={reactivating}
                    onUpdatePayment={() => onUpdatePayment?.()}
                    onCancel={() => onCancel?.()}
                    onReactivate={onReactivate}
                    premiumCampaignsAtRisk={initialPlan.premium_campaign_impact?.campaigns ?? []}
                />
            )}

            <DialogueSheet
                onClose={handleDowngradeModal}
                open={showDowngradeModal}
                onAgreeClicked={() => {
                    setOpen(!open);
                    handleDowngradeModal();
                }}
                onDisagreeClick={handleDowngradeModal}
                header="Downgrade Subscription?"
                body={formatPremiumCampaignImpactBody(
                    CREATOR_SUBSCRIPTION_MANAGE_COPY.downgradePremiumCampaignWarning,
                    downgradeCampaignTitles,
                    'You are about to switch to a lower plan. Your current plan benefits apply until the change takes effect.',
                )}
                positiveText="Continue downgrade"
                negativeText="Keep current plan"
            />

            <Stack alignItems="flex-start" spacing={3} sx={{ width: '100%' }}>
                {showManageSubscription && (
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            px: 2.5,
                            py: 2,
                            borderRadius: 3,
                            border: `1px solid ${alpha(t.highlight, 0.2)}`,
                            background: `linear-gradient(135deg, ${alpha(t.highlight, 0.08)} 0%, ${alpha(
                                t.highlight,
                                0.03,
                            )} 100%)`,
                            boxShadow: `inset 0 0 0 1px ${alpha(t.highlight, 0.06)}`,
                        }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 2,
                                    bgcolor: alpha(t.highlight, 0.15),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        bgcolor: t.highlight,
                                        boxShadow: `0 0 8px ${alpha(t.highlight, 0.6)}`,
                                    }}
                                />
                            </Box>
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: '0.6875rem',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        letterSpacing: '0.1em',
                                        textTransform: 'uppercase',
                                        color: t.highlight,
                                        lineHeight: 1,
                                        mb: 0.5,
                                    }}>
                                    Active plan
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '1rem',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: t.primary_white,
                                        lineHeight: 1.2,
                                    }}>
                                    {initialPlan?.plan_name}
                                </Typography>
                            </Box>
                        </Box>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                trackSubscriptionAction(MIXPANEL_EVENT.MANAGE_SUBSCRIPTION_CLICK, {
                                    buttonName: 'MANAGE',
                                    buttonAction: 'MANAGE_SUBSCRIPTION',
                                    subscriptionType: 'creator',
                                });
                                setManageModalOpen(true);
                            }}
                            sx={{
                                flexShrink: 0,
                                borderColor: alpha(t.tag_white, 0.2),
                                color: alpha(t.tag_white, 0.75),
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: '0.8125rem',
                                textTransform: 'none',
                                borderRadius: 2,
                                px: 1.5,
                                '&:hover': {
                                    borderColor: t.highlight,
                                    color: t.highlight,
                                    bgcolor: alpha(t.highlight, 0.06),
                                },
                            }}>
                            Manage
                        </Button>
                    </Box>
                )}

                {showCanceledStrip && initialPlan && (
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            px: 2.5,
                            py: 2,
                            borderRadius: 3,
                            border: `1px solid ${alpha(t.tag_white, 0.12)}`,
                            background: `linear-gradient(135deg, ${alpha(t.tag_white, 0.06)} 0%, ${alpha(
                                t.tag_white,
                                0.02,
                            )} 100%)`,
                        }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 2,
                                    bgcolor: alpha(t.tag_white, 0.08),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        bgcolor: alpha(t.tag_white, 0.35),
                                    }}
                                />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontSize: '0.6875rem',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        letterSpacing: '0.1em',
                                        textTransform: 'uppercase',
                                        color: alpha(t.tag_white, 0.55),
                                        lineHeight: 1,
                                        mb: 0.5,
                                    }}>
                                    {canceledStillInPeriod
                                        ? CREATOR_SUBSCRIPTION_MANAGE_COPY.canceledStripEyebrowActive
                                        : CREATOR_SUBSCRIPTION_MANAGE_COPY.canceledStripEyebrowEnded}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '1rem',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: t.primary_white,
                                        lineHeight: 1.2,
                                    }}>
                                    {initialPlan.plan_name ?? initialPlan.name}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '0.8125rem',
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: alpha(t.tag_white, 0.65),
                                        mt: 0.35,
                                        lineHeight: 1.35,
                                    }}>
                                    {getCreatorCanceledStripSubtitle(canceledPeriodEnd, canceledEndLabel)}
                                </Typography>
                            </Box>
                        </Box>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                trackSubscriptionAction(MIXPANEL_EVENT.SUBSCRIBE_CLICK, {
                                    buttonName: 'SUBSCRIBE AGAIN',
                                    buttonAction: 'SUBSCRIBE_AGAIN',
                                    subscriptionType: 'creator',
                                });
                                if (onAuthRequired) {
                                    onAuthRequired();
                                    return;
                                }
                                const code = resolvePlanCode(initialPlan);
                                const match =
                                    (code &&
                                        plans?.find(p => p.plan_code === code || String(p.plan?.id ?? '') === code)) ||
                                    plans?.find(
                                        p => (p.plan_name ?? p.name) === (initialPlan.plan_name ?? initialPlan.name),
                                    );
                                if (!match) return;
                                setSelectedPreliminaryPlan(match);
                                setOpen(true);
                                onPlanSelect?.();
                            }}
                            sx={{
                                flexShrink: 0,
                                borderColor: alpha(t.highlight, 0.35),
                                color: t.highlight,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: '0.8125rem',
                                textTransform: 'none',
                                borderRadius: 2,
                                px: 1.5,
                                '&:hover': {
                                    borderColor: t.highlight,
                                    color: t.highlight,
                                    bgcolor: alpha(t.highlight, 0.06),
                                },
                            }}>
                            {CREATOR_SUBSCRIPTION_MANAGE_COPY.canceledStripCta}
                        </Button>
                    </Box>
                )}

                <Stack
                    direction="row"
                    alignItems="center"
                    flexWrap="nowrap"
                    spacing={2}
                    sx={{
                        gap: { xs: 0.75, sm: 2 },
                        width: '100%',
                        justifyContent: 'center',
                        whiteSpace: 'nowrap',
                        '@media (min-width: 1000px) and (max-width: 1060px) and (min-height: 600px) and (max-height: 1366px)':
                            {
                                gap: 0.5,
                            },
                    }}>
                    <Typography
                        sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontWeight: 600,
                            letterSpacing: { xs: '0.06em', sm: '0.14em' },
                            textTransform: 'uppercase',
                            color: t.tag_white,
                            opacity: isYearly ? 0.5 : 1,
                            whiteSpace: 'nowrap',
                        }}>
                        Monthly
                    </Typography>
                    <Box
                        role="switch"
                        aria-checked={isYearly}
                        tabIndex={0}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                const next = isYearly ? 'month' : 'year';
                                trackSubscriptionAction(MIXPANEL_EVENT.TOGGLE_SUBSCRIPTION_INTERVAL_CLICK, {
                                    buttonName: next === 'year' ? 'YEARLY' : 'MONTHLY',
                                    buttonAction: 'TOGGLE_SUBSCRIPTION_INTERVAL',
                                    subscriptionType: 'creator',
                                    interval: next,
                                });
                                setVisibleTab(next);
                            }
                        }}
                        onClick={() => {
                            const next = isYearly ? 'month' : 'year';
                            trackSubscriptionAction(MIXPANEL_EVENT.TOGGLE_SUBSCRIPTION_INTERVAL_CLICK, {
                                buttonName: next === 'year' ? 'YEARLY' : 'MONTHLY',
                                buttonAction: 'TOGGLE_SUBSCRIPTION_INTERVAL',
                                subscriptionType: 'creator',
                                interval: next,
                            });
                            setVisibleTab(next);
                        }}
                        sx={{
                            width: { xs: 44, sm: 56 },
                            height: { xs: 24, sm: 28 },
                            flexShrink: 0,
                            borderRadius: 999,
                            bgcolor: alpha(t.tag_white, darkModeEnabled ? 0.12 : 0.15),
                            border: `1px solid ${alpha(t.tag_white, 0.12)}`,
                            cursor: 'pointer',
                            position: 'relative',
                            transition: 'background-color 0.2s ease',
                        }}>
                        <Box
                            sx={{
                                position: 'absolute',
                                top: { xs: 3, sm: 4 },
                                left: { xs: isYearly ? 23 : 3, sm: isYearly ? 30 : 4 },
                                width: { xs: 18, sm: 20 },
                                height: { xs: 18, sm: 20 },
                                borderRadius: '50%',
                                bgcolor: t.highlight,
                                transition: 'left 0.25s ease',
                                boxShadow: `0 1px 4px ${alpha(t.highlight, 0.45)}`,
                            }}
                        />
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="nowrap">
                        <Typography
                            sx={{
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontWeight: 600,
                                letterSpacing: { xs: '0.06em', sm: '0.14em' },
                                textTransform: 'uppercase',
                                color: t.tag_white,
                                opacity: isYearly ? 1 : 0.5,
                                whiteSpace: 'nowrap',
                            }}>
                            Yearly
                        </Typography>
                        <Typography
                            component="span"
                            sx={{
                                fontSize: { xs: '8px', sm: '10px' },
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontWeight: 800,
                                letterSpacing: { xs: '0.03em', sm: '0.06em' },
                                textTransform: 'uppercase',
                                color: t.highlight,
                                bgcolor: alpha(t.highlight, 0.2),
                                px: { xs: 0.75, sm: 1 },
                                py: { xs: 0.35, sm: 0.5 },
                                borderRadius: 999,
                                whiteSpace: 'nowrap',
                            }}>
                            Save 20%
                        </Typography>
                    </Stack>
                </Stack>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: isPromptMode
                            ? {
                                  xs: '1fr',
                                  md: `repeat(${promptColumns}, minmax(0, 1fr))`,
                              }
                            : { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                        gap: isPromptMode ? { xs: 2, md: 2 } : 3,
                        width: '100%',
                        alignItems: 'stretch',
                        minHeight: 0,
                        '@media (min-width: 1000px) and (max-width: 1060px) and (min-height: 600px) and (max-height: 1366px)':
                            {
                                gridTemplateColumns: isPromptMode ? `repeat(${promptColumns}, minmax(0, 1fr))` : '1fr',
                            },
                    }}>
                    {plan[visisbleTab as keyof ISortedPlan]?.map((planItem, index) => (
                        <Box
                            key={`${planItem.plan_code}-${index}`}
                            sx={{
                                minWidth: 0,
                                height: '100%',
                                display: 'flex',
                                alignItems: 'stretch',
                            }}>
                            <PlanCard
                                plan={planItem}
                                planIndex={index}
                                compact={isPromptMode}
                                isSelected={isCreatorCatalogPlanSelected(planItem, initialPlan)}
                                onSelect={() => {
                                    if (isCreatorCatalogPlanSelected(planItem, initialPlan)) {
                                        return;
                                    }
                                    trackSubscriptionAction(MIXPANEL_EVENT.SELECT_SUBSCRIPTION_PLAN_CLICK, {
                                        buttonName: String(planItem.plan_name ?? planItem.name ?? 'PLAN').toUpperCase(),
                                        buttonAction: 'SELECT_SUBSCRIPTION_PLAN',
                                        subscriptionType: 'creator',
                                        planCode: planItem.plan_code ?? planItem.plan?.id ?? null,
                                        planName: planItem.plan_name ?? planItem.name ?? null,
                                        interval: visisbleTab,
                                        amount: planItem.amount,
                                    });
                                    if (
                                        initialPlan &&
                                        !isTerminalCreatorSubscriptionStatus(initialPlan.status) &&
                                        (initialPlan.plan?.amount ?? initialPlan.amount) > planItem.amount
                                    ) {
                                        const lost = filterPremiumCampaignsLostOnPlan(
                                            initialPlan.premium_campaign_impact?.campaigns,
                                            resolveCreatorEntitlements(
                                                planItem.plan_name ?? planItem.name,
                                                planItem.entitlements,
                                                planItem.effective_entitlements,
                                            ),
                                        );
                                        setDowngradeCampaignTitles(lost.map(c => c.title));
                                        handleDowngradeModal();
                                        setSelectedPreliminaryPlan(planItem);
                                        return;
                                    }
                                    if (onAuthRequired) {
                                        onAuthRequired();
                                        return;
                                    }
                                    onPlanSelect?.();
                                    setSelectedPreliminaryPlan(planItem);
                                    setOpen(!open);
                                }}
                            />
                        </Box>
                    ))}
                </Box>
            </Stack>
        </Box>
    );
};

export { AccountSubscriptions };
